import type {
  EtatAcheminement,
  ModeTransmission,
  RangementPiece,
  ReglementPiece,
  Remplissage,
  RubriqueDrive,
  SourcePiece,
  StatutPiece,
  TransmissionPiece,
  TypePiece,
} from './domaine'
import { normaliserLibelle } from './libelle'
import { contient, type ExtractionPiece, nomNormalise, sirenDe } from './pieces'

// Acheminement d'une pièce rattachée : notre système identifie, archive et route ; le cabinet
// (Tiime) fait la comptabilité. Deux destinations indépendantes, chacune avec son statut et
// ses essais : le Drive (archive, toujours) et le cabinet (factures d'achat : adresse de
// collecte, ou dépôt direct dans Tiime). L'échec de l'une ne bloque ni ne masque l'autre.

// Rangement dans le Drive

/** Dossier de rangement selon la nature de la pièce. */
export function rubriqueDrive(p: { type: TypePiece; emise: boolean }): RubriqueDrive {
  if (p.type === 'facture') return p.emise ? 'ventes' : 'achats'
  if (p.type === 'releve') return 'releves'
  return 'autres'
}

export type CheminsDrive = Record<RubriqueDrive, string | null>

/**
 * Dossiers d'un chemin, relatif à la racine du Drive partagé de l'entité. Le début fixe (avant
 * la première variable) doit déjà exister : l'appli ne crée jamais d'arborescence à côté de
 * celle en place. La suite ({AAAA}, {AAAAMM}…) est créée au besoin.
 */
export type CheminResolu = { existants: string[]; aCreer: string[] }

const VARIABLES = ['AAAA', 'MM', 'AAAAMM'] as const

export function resoudreChemin(modele: string, jour: string): CheminResolu | { erreur: string } {
  const segments = modele.split('/').filter((s) => s.trim() !== '')
  if (segments.length === 0) return { erreur: 'Chemin vide.' }
  if (segments.some((s) => s.trim() === '..' || s.trim() === '.'))
    return { erreur: 'Dossier « .. » interdit dans un chemin.' }
  const inconnue = modele.match(/\{([^}]*)\}/g)?.find((v) => !VARIABLES.some((x) => `{${x}}` === v))
  if (inconnue)
    return {
      erreur: `Variable inconnue : ${inconnue}. Variables possibles : ${VARIABLES.map((v) => `{${v}}`).join(', ')}.`,
    }
  const valeurs: Record<(typeof VARIABLES)[number], string> = {
    AAAA: jour.slice(0, 4),
    MM: jour.slice(5, 7),
    AAAAMM: `${jour.slice(0, 4)}${jour.slice(5, 7)}`,
  }
  const remplacer = (s: string) =>
    s.replace(/\{(AAAAMM|AAAA|MM)\}/g, (_, v: keyof typeof valeurs) => valeurs[v])
  const premiereVariable = segments.findIndex((s) => s.includes('{'))
  const fin = premiereVariable === -1 ? segments.length : premiereVariable
  return {
    existants: segments.slice(0, fin),
    aCreer: segments.slice(fin).map(remplacer),
  }
}

/**
 * Chemin de la rubrique, sinon celui des « autres » ; null si aucun n'est renseigné (le
 * rangement attend qu'il le soit).
 */
export function cheminDrive(
  chemins: CheminsDrive,
  rubrique: RubriqueDrive,
  jour: string,
): CheminResolu | { erreur: string } | null {
  const modele = chemins[rubrique] ?? chemins.autres
  return modele ? resoudreChemin(modele, jour) : null
}

// Nom de fichier

const TYPES_NOM: Record<TypePiece, string> = {
  facture: 'FACTURE',
  avis: 'AVIS',
  courrier: 'COURRIER',
  accuse_depot: 'ACCUSE',
  releve: 'RELEVE',
  contrat: 'CONTRAT',
  fiche_paie: 'PAIE',
  autre: 'PIECE',
}

/** Morceau de nom de fichier : lettres et chiffres sans accents, séparés par des tirets. */
function segment(texte: string | null, max = 40): string {
  if (!texte) return ''
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max)
    .replace(/-+$/g, '')
}

function montantNom(cts: number, devise: string): string {
  const signe = cts < 0 ? '-' : ''
  const abs = Math.abs(cts)
  return `${signe}${Math.floor(abs / 100)},${String(abs % 100).padStart(2, '0')}${devise}`
}

/**
 * Nom de rangement, déterministe : « AAAA-MM-JJ_Fournisseur_1234,56EUR_FACTURE-12345.pdf »
 * pour une facture (le client pour une vente), « AAAA-MM-JJ_Emetteur_AVIS-objet.pdf » sinon.
 */
export function nomFichierPiece(p: {
  extraction: ExtractionPiece | null
  emise: boolean
  recueLe: string
  extension?: string
}): string {
  const x = p.extraction
  const extension = p.extension ?? 'pdf'
  const jour = x?.dateDocument ?? p.recueLe
  const type = TYPES_NOM[x?.type ?? 'autre']
  if (!x) return `${jour}_inconnu_${type}.${extension}`
  const qui = segment(p.emise ? x.destinataire.nom : x.emetteur.nom) || 'inconnu'
  const morceaux = [jour, qui]
  if (x.type === 'facture') {
    // Le total de la facture : un reçu déjà payé (à payer : 0) ne s'appelle pas « 0,00 ».
    const montant = x.montants.ttcCts ?? x.montants.aPayerCts
    if (montant !== null) morceaux.push(montantNom(montant, x.devise ?? 'EUR'))
    const numero = segment(x.numero)
    morceaux.push(numero ? `${type}-${numero}` : type)
  } else {
    const objet = segment(x.objet)
    morceaux.push(objet ? `${type}-${objet}` : type)
  }
  return `${morceaux.join('_')}.${extension}`
}

/** Nom déjà pris par un autre fichier : « nom_2.pdf », « nom_3.pdf »… */
export function suffixerNom(nom: string, rang: number): string {
  const point = nom.lastIndexOf('.')
  return point > 0 ? `${nom.slice(0, point)}_${rang}${nom.slice(point)}` : `${nom}_${rang}`
}

// Transmission au cabinet (adresse de collecte Tiime, ou dépôt direct dans Tiime)

/**
 * Ce que la collecte Tiime accepte : PDF et images, 8 Mo au plus par fichier ; au-delà, dépôt à
 * la main.
 */
export const TYPES_MIME_COLLECTE = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif']
export const TAILLE_MAX_COLLECTE = 8 * 1024 * 1024

export function acceptePourCollecte(f: { typeMime: string; taille: number | null }): boolean {
  return (
    TYPES_MIME_COLLECTE.includes(f.typeMime) &&
    (f.taille === null || f.taille <= TAILLE_MAX_COLLECTE)
  )
}

/**
 * Ce que le dépôt direct accepte : PDF, JPEG et PNG, 8 Mo au plus, les types que la remise du
 * fichier à l'agent reconnaît à ses premiers octets. Une GIF, que la collecte par mail accepte,
 * se dépose à la main.
 */
export const TYPES_MIME_DEPOT_DIRECT = ['application/pdf', 'image/jpeg', 'image/png'] as const
export type TypeMimeDepotDirect = (typeof TYPES_MIME_DEPOT_DIRECT)[number]

export function acceptePourDepotDirect(f: { typeMime: string; taille: number | null }): boolean {
  return (
    (TYPES_MIME_DEPOT_DIRECT as readonly string[]).includes(f.typeMime) && acceptePourCollecte(f)
  )
}

/**
 * Le cabinet de l'entité tient sa comptabilité sur Tiime : il y reçoit les justificatifs par
 * mail (adresse de collecte) ou par dépôt direct ; jamais une entité sans envoi.
 */
export function surTiime(mode: ModeTransmission): boolean {
  return mode === 'mail' || mode === 'tiime'
}

/**
 * Faut-il transmettre la pièce au cabinet ? Seulement les factures d'achat d'une entité dont le
 * cabinet les reçoit (par mail à l'adresse de collecte, ou par dépôt direct dans Tiime : mêmes
 * exclusions), jamais ce qu'il a déjà (plateforme agréée), jamais une vente (elle sort de
 * Tiime). Reçue dans une boîte personnelle : un humain dit d'abord si la société l'a réglée ;
 * avancée par la personne, c'est une note de frais, créée à la main.
 * Espace privé : jamais de transmission automatique ; par mail, elle partirait d'admin@, la boîte
 * des administrateurs (pièce jointe et nom de l'espace dans ses Envoyés, I1), et le dépôt direct
 * exclut l'espace privé (CLAUDE.md) ; à déposer soi-même.
 * Facture d'un portail d'achats (`payeurAttendu`) : rien ne part avant que la décision « qui a
 * payé » ait dit si l'entité l'a réglée elle-même ou si c'est une avance (packages/core/src/cartes.ts).
 * Dépôt direct : `a_faire` sans adresse de collecte ; la pièce attend son opération, déposée et
 * reliée par l'agent du Mac quand la règle de rapprochement la dit sûre. Le dépôt direct ne prend
 * que PDF, JPEG et PNG (`acceptePourDepotDirect`) : une GIF y est `manuelle`.
 */
export function transmissionPrevue(
  piece: {
    type: TypePiece
    emise: boolean
    source: SourcePiece
    dejaChezCabinet: boolean
    typeMime: string
    taille: number | null
    boitePersonnelle: boolean
    reglement: ReglementPiece | null
    payeurAttendu?: boolean
  },
  entite: { transmission: ModeTransmission; adresseCollecte: string | null; privee?: boolean },
): Exclude<TransmissionPiece, 'faite' | 'incertain'> {
  if (entite.transmission === 'aucune') return 'sans_objet'
  if (piece.source === 'plateforme' || piece.dejaChezCabinet) return 'sans_objet'
  if (piece.type !== 'facture' || piece.emise) return 'sans_objet'
  if (piece.reglement === 'avance') return 'sans_objet'
  if ((piece.boitePersonnelle || piece.payeurAttendu) && piece.reglement === null)
    return 'a_decider'
  const accepte =
    entite.transmission === 'mail' ? acceptePourCollecte(piece) : acceptePourDepotDirect(piece)
  if (entite.privee || !accepte) return 'manuelle'
  // Par mail, il faut l'adresse de collecte ; le dépôt direct n'en attend aucune.
  if (entite.transmission === 'mail') return entite.adresseCollecte ? 'a_faire' : 'en_attente'
  return 'a_faire'
}

// Doublons

export type FacturePourDoublon = {
  emetteurSiren: string | null
  emetteurNom: string | null
  numero: string | null
  /** Identifiant du mail d'origine. */
  messageId: string | null
  montantCts: number | null
  dateDocument: string | null
  /** Nom d'arrivée : jamais une preuve de doublon (« Facture.pdf » partout), seulement un indice de reçu. */
  nomFichier: string | null
  /** Type de la pièce lue, quand il compte (`deuxDocumentsDuMemeContrat`) ; inconnu : une facture. */
  type?: string | null
}

const numeroCompact = (n: string | null) =>
  n ? normaliserLibelle(n).replace(/[^A-Z0-9]/g, '') : ''

function memeFournisseur(a: FacturePourDoublon, b: FacturePourDoublon): boolean {
  const na = nomNormalise(a.emetteurNom)
  const nb = nomNormalise(b.emetteurNom)
  if (!na || !nb) return false
  return na === nb || contient(na, nb) || contient(nb, na)
}

const RECU = /(^|[^A-Z])(RECEIPT|RECU|TICKET)([^A-Z]|$)/

const ressembleAUnRecu = (nomFichier: string | null) =>
  nomFichier !== null && RECU.test(normaliserLibelle(nomFichier))

/**
 * Hors facture, le numéro lu est souvent celui du contrat ou du compte (attestation annuelle,
 * relevé, tableau d'amortissement d'un même prêt) : à deux dates différentes, ce sont deux
 * documents, pas un même document renvoyé.
 */
function deuxDocumentsDuMemeContrat(a: FacturePourDoublon, b: FacturePourDoublon): boolean {
  if (!a.type || !b.type || a.type === 'facture' || b.type === 'facture') return false
  return a.dateDocument !== null && b.dateDocument !== null && a.dateDocument !== b.dateDocument
}

/**
 * Deux pièces décrivent-elles sûrement la même facture ? Jamais par le nom de fichier ni par
 * l'empreinte seule (un même document renvoyé change souvent de métadonnées) : par son
 * contenu lu. Même SIREN émetteur et même numéro ; sans SIREN, même fournisseur (au nom) et
 * même numéro ; ou, dans un même mail, même fournisseur et même montant (facture et reçu d'un
 * même paiement), sauf deux numéros différents sans reçu parmi elles (deux factures).
 */
export function estDoublon(a: FacturePourDoublon, b: FacturePourDoublon): boolean {
  const numA = numeroCompact(a.numero)
  const numB = numeroCompact(b.numero)
  if (numA.length >= 3 && numA === numB && !deuxDocumentsDuMemeContrat(a, b)) {
    const sirenA = sirenDe(a.emetteurSiren)
    const sirenB = sirenDe(b.emetteurSiren)
    if (sirenA && sirenB) return sirenA === sirenB
    if (memeFournisseur(a, b)) return true
  }
  const numerosDifferents = numA.length >= 3 && numB.length >= 3 && numA !== numB
  if (numerosDifferents && !ressembleAUnRecu(a.nomFichier) && !ressembleAUnRecu(b.nomFichier))
    return false
  return (
    a.messageId !== null &&
    a.messageId === b.messageId &&
    a.montantCts !== null &&
    a.montantCts === b.montantCts &&
    memeFournisseur(a, b)
  )
}

/**
 * Probablement la même pièce, sans certitude : même fournisseur, même date, même montant,
 * sans deux numéros qui les distinguent (ticket renvoyé, PDF régénéré). Un humain tranche.
 */
export function doublonProbable(a: FacturePourDoublon, b: FacturePourDoublon): boolean {
  if (estDoublon(a, b)) return false
  const numA = numeroCompact(a.numero)
  const numB = numeroCompact(b.numero)
  if (numA.length >= 3 && numB.length >= 3) return false
  return (
    a.montantCts !== null &&
    a.montantCts === b.montantCts &&
    a.dateDocument !== null &&
    a.dateDocument === b.dateDocument &&
    memeFournisseur(a, b)
  )
}

/**
 * Laquelle garder comme pièce de référence : la facture plutôt que le reçu ; à égalité, celle
 * déjà connue.
 */
export function prefererReference(
  existante: FacturePourDoublon,
  nouvelle: FacturePourDoublon,
): 'existante' | 'nouvelle' {
  return ressembleAUnRecu(existante.nomFichier) && !ressembleAUnRecu(nouvelle.nomFichier)
    ? 'nouvelle'
    : 'existante'
}

// État et relances

/** Au troisième essai manqué, la destination est « en échec » et une alerte part. */
export const ESSAIS_AVANT_ALERTE = 3

const DELAIS_MINUTES = [0, 1, 5, 15, 60, 240, 360]

/** Minutes à attendre avant l'essai suivant, après `tentatives` échecs. */
export function delaiAvantEssai(tentatives: number): number {
  return DELAIS_MINUTES[Math.min(tentatives, DELAIS_MINUTES.length - 1)]!
}

/** Où en est une pièce, de la réception au Drive et au cabinet. */
export function etatAcheminement(p: {
  statut: StatutPiece
  rangement: RangementPiece | null
  rangementTentatives: number
  transmission: TransmissionPiece | null
  transmissionTentatives: number
  remplissage?: Remplissage | null
}): EtatAcheminement {
  // Version remplie pas encore relue : ni rangée, ni envoyée, en attente d'un humain.
  if (p.remplissage === 'brouillon') return 'a_valider'
  if (p.statut === 'recue' || p.statut === 'extraite') return 'en_lecture'
  if (p.statut === 'a_verifier') return 'a_verifier'
  if (p.statut === 'doublon') return 'doublon'
  if (p.statut === 'ignoree') return 'rejetee'
  const t = p.transmission ?? 'sans_objet'
  if (t === 'a_decider' || t === 'incertain' || t === 'manuelle') return 'a_decider'
  const driveFait = p.rangement === 'fait'
  const tiimeFait = t === 'faite' || t === 'sans_objet'
  if (!driveFait && p.rangementTentatives >= ESSAIS_AVANT_ALERTE) return 'drive_echec'
  if (!tiimeFait && p.transmissionTentatives >= ESSAIS_AVANT_ALERTE) return 'tiime_echec'
  if (p.rangement === 'en_attente' || t === 'en_attente') return 'en_attente'
  if (driveFait && tiimeFait) return 'termine'
  if (driveFait || t === 'faite') return 'partiel'
  return 'en_cours'
}
